import './App.css';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  ListItemIcon,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { createMuiTheme, MuiThemeProvider, useTheme } from '@material-ui/core/styles';
import {
  AccountCircle as AccountCircleIcon,
  Add as AddIcon,
  ExitToApp as ExitToAppIcon,
  Lock as LockIcon,
} from '@material-ui/icons';
import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { setSettings } from './Api'
import { createPlaylist, deletePlaylist, getLogin, getPlaylists, renamePlaylist } from './ApiManager';
import AuthContext, { AuthProvider } from './AuthContext';
import logoMedia from './logo-mediatheque-charente.png';
import MedialistManager from './MedialistManager';
import SearchPanel from './SearchPanel';
import SigninPage from './SigninPage';
import Tooltip from '@material-ui/core/Tooltip';

// const users = [
//   { userid: '0e60a39c64ffba54256ce180ed52c9b8', name: 'Caroline Joly'},
//   { userid: '8MXUU0PV8NLKNWVNW2PRQYCKI182EY0EAQ4PNDLQ', name: 'pierre' },
//   { userid: '94A6G4ANTLSUEJZHNPQVG02SN01OYULWBMXHAVOI', name: 'hervé' },
// ]

const theme = createMuiTheme({
  palette: {
    primary: {
      // light: '#26ada6',
      main: '#26ada6',
      contrastText: 'white',
      // dark: '#26ada6',
    },
    secondary: {
      main: '#7e57c2',
      contrastText: 'white',
    }
  }
})

// const styles = (theme: Theme) => ({
//   modal: {
//     position: 'absolute',
//     width: theme.spacing.unit * 50,
//     backgroundColor: theme.palette.background.paper,
//     boxShadow: theme.shadows[5],
//     padding: theme.spacing.unit * 4,
//   },
// });

// async function fetchLogin(email: string, pwd: string, rememberme: boolean) {
//   const url = `${baseUrl}/signin?login=${email}&password=${pwd}&${suffixUrl}`
//   const result = await fetch(url)
//   const json = await result.json()
//   return json
// }

function SelectPlaylist({ userId, playlistId, playlists, foreignPlaylists, onChange }: {
  userId: string | null,
  playlistId: string | null,
  playlists: { id: string, name: string }[],
  foreignPlaylists: { id: string, name: string }[],
  onChange: (val: string)=>void,
}) {
  const [labelWidth, setLabelWidth] = useState(0)
  const ref = useRef(null)
  useEffect(() => {
    if (!ref.current)
      return
    const node = ReactDOM.findDOMNode(ref.current) as HTMLElement
    if (!node)
      return
    setLabelWidth((node as unknown as HTMLElement).offsetWidth)
  }, [])

  return <FormControl variant='outlined' style={{ width: '100%' }}>
    <InputLabel htmlFor='select-playlist' ref={ref}>Playlists</InputLabel>
    <Select      
      value={playlistId || ''}
      onChange={evt => onChange(evt.target.value as string)}
      disabled={!userId}
      input={
        <OutlinedInput
          name='playlist'
          id='select-playlist'
          labelWidth={labelWidth} />
      }
    >
      <MenuItem value={''}>
        <em>Sélectionnez une playlist ci-dessous</em>
      </MenuItem>
      {
        playlists.map(item => 
        <MenuItem key={item.id}
                  value={item.id}>
          {item.name}
        </MenuItem>)
      }
      {foreignPlaylists.length > 0 ? [
        <Divider key='divider-0' />,
        <Typography key='typo-0' variant='overline' style={{ marginLeft: '16px' }}>Playlists de vos collègues</Typography>,
      ] : null}
      {
        foreignPlaylists.map(item => 
        <MenuItem key={item.id}
                  value={item.id}>
          <ListItemIcon>
            <LockIcon />
          </ListItemIcon>
          {item.name}
        </MenuItem>)
      }
    </Select>
  </FormControl>
}
const SelectPlaylistMemo = memo(SelectPlaylist)

export default function AuthentifiedApp() {
  const [auth, setAuth] = useState({
    isLogged: false,
    username: '',
    user: undefined as any,
    library: undefined as any,
    settings: undefined as any,
    error: undefined as string | undefined,
  })

  const _login = async (username: string, pwd: string, rememberme: boolean) => {
    let info;
    try {
      info = await getLogin(username, pwd)
      console.log('info', info)
    } catch (err) {
      throw err
    }
    if (info.code === 1) {
      setAuth({
        isLogged: false,
        username: '',
        user: undefined,
        library: undefined,
        settings: undefined,
        error: 'Identifiants vides',
      })
      throw info
    } else if (info.code === 201) {
      setAuth({
        isLogged: false,
        username: '',
        user: undefined,
        library: undefined,
        settings: undefined,
        error: 'Identifiants invalides',
      })
      throw info
    } else if (info.meta.user_status === 1) {
      // ok
      setSettings(info.results.settings)
      setAuth({
        isLogged: true,
        username: username,
        user: info.results.user,
        library: info.results.library,
        settings: info.results.settings,
        error: undefined,
      })
    } else if (info.meta.user_status !== 1) {
      setAuth({
        isLogged: false,
        username: '',
        user: undefined,
        library: undefined,
        settings: undefined,
        error: 'Compte désactivé',
      })
      throw info
    }
  }

  const _logout = () => {
    localStorage.removeItem('remember-me')
    setAuth({
      isLogged: false,
      username: '',
      user: undefined,
      library: undefined,
      settings: undefined,
      error: undefined,
    })
  }

  return <AuthProvider value={{ ...auth, login: _login, logout: _logout }}>
    <ThemedApp />
  </AuthProvider>
}

export function ThemedApp() {
  return <MuiThemeProvider theme={theme}>
    <App />
  </MuiThemeProvider>
}

export function App() {
  const theme = useTheme()
  const [playlistId, setPlaylistId] = useState(null as (string | null))
  const [createPlaylistOpen, setCreatePlaylistOpen] = useState(false)
  const [playlists, setPlaylists] = useState([] as { id: string, name: string, self: boolean }[])
  // const [currentTrack, setCurrentTrack] = useState(undefined as number | undefined)
  const { isLogged, user, logout, settings } = useContext(AuthContext)
  const userId = user && user.id

  useEffect(() => {
    if (!isLogged || !user)
      return
    getPlaylists(userId).then(pls => {
      setPlaylists((pls.results || []).map(pl => ({
        id: pl.id,
        name: pl.name,
        self: pl.owner_id === userId,
      })))
    })
  }, [userId, isLogged, user])

  const onOpenCreatePlaylist = () => {
    setCreatePlaylistOpen(true)
  }
  const onCloseCreatePlaylist = () => {
    setCreatePlaylistOpen(false)
  }
  const onValidCreatePlaylist = async (name: string) => {
    if (name !== '') {
      const response = await createPlaylist(userId!, name)
      console.log('response', response)
      setPlaylists([...playlists, {...response.item, id: 'pl-' + response.item.id, self:true}])
      setPlaylistId('pl-' + response.item.id.toString())
    }
  }

  const onRemovePlaylist = async () => {
    await deletePlaylist(userId!, playlistId!)
    const newPlaylists = playlists.filter(p => p.id.toString() !== playlistId!)
    setPlaylistId(newPlaylists.length > 0
      ? newPlaylists[0].id.toString()
      : null)
    setPlaylists(newPlaylists)
  }
  
  const onRenamePlaylist = async (newName:  string) => {
    await renamePlaylist(userId!, playlistId!, newName)
    for (let playlist of playlists) {
      if (playlist.id.toString() === playlistId!) {
        playlist.name = newName
        break
      }
    }
    const newPlaylists = [ ...playlists ]
    setPlaylists(newPlaylists)
  }

  if (!isLogged)
    return <SigninPage />

  console.log('PLAYLIST ID', playlistId)
  console.log('playlists', playlists)

  return <div className='App'>
    <Grid container alignItems="center" >
      <Grid container item xs={12} style={{
        backgroundColor: settings && settings.bg_color || theme.palette.primary.main,
        color: 'white',
        height: 48,
        lineHeight: 48,
        backgroundImage: `url(https://mmcdn.hosting-media.net/pict/medianum/logo-${settings && settings.partner_name || 'demo'}.png)`,
        backgroundSize: 'auto 76%',
        backgroundPosition: '16px center',
        backgroundRepeat: 'no-repeat',
      }} alignItems='center' alignContent='center' justify='center'>
        <Typography variant='body1' style={{ textTransform: 'uppercase', flexGrow: 1 }}>
          Éditeur de playlist
        </Typography>
        <Typography style={{ flexGrow: 0 }}>
          { user ? user.firstname + ' ' + user.lastname : '' }
        </Typography>
        <AccountCircleIcon style={{ flexGrow: 0, marginLeft: 16, marginRight: 16 }} />
        <Tooltip title='Déconnexion'>
          <ExitToAppIcon style={{ flexGrow: 0, marginRight: 16, cursor: 'pointer' }} onClick={logout} />
        </Tooltip>
      </Grid>
    </Grid>  
    <div style={{ padding: 16, marginTop: 8 }} >
    <Grid container spacing={1} style={{}}>

      <Grid item xs={8}>
        <SearchPanel  />
      </Grid>

      <Grid item xs={4} style={{  }}>
        <SelectPlaylistMemo
          userId={userId}
          playlistId={playlistId}
          playlists={playlists.filter(pl => pl.self)}
          foreignPlaylists={playlists.filter(pl => !pl.self)}
          onChange={setPlaylistId} />
        {
          // overflowY: 'auto',
          // boxSizing: 'border-box',
          // maxHeight: '100vh',
          // position: 'fixed',
          // top: 0,
          // right: 0,
          // bottom: 0,
          (userId && playlistId)
          ? <Grid item xs={12} style={{}}>
            {/* <CreatePlayerIframe
              playlistId={playlistId}
              idx={currentTrack}
              width={300}
              height={177}/> */}
            <MedialistManager
              userId={userId}
              isOwner={playlists ? playlists.filter(pl => pl.id === playlistId)[0].self : false}
              playlistId={playlistId}
              onRemovePlaylist={onRemovePlaylist}
              onRenamePlaylist={onRenamePlaylist} />
          </Grid>
          : null
        }
      </Grid>

    </Grid>
    <Fab
    color='secondary'
    aria-label='Add'
    style={{ position: 'fixed', right: 16, bottom: 16 }}
    onClick={onOpenCreatePlaylist}
    disabled={!userId}>
      <AddIcon />
    </Fab>
    <CreatePlaylistDialog
      open={createPlaylistOpen}
      onClose={onCloseCreatePlaylist}
      onValid={onValidCreatePlaylist}
    />
    </div>
  </div>
}

function CreatePlaylistDialog({ open, onClose, onValid }: {
  open: boolean,
  onClose: ()=>void,
  onValid: (name: string)=>void,
}) {
  const [name, setName] = useState('')

  return <Dialog open={open} onClose={() => {
    setName('')
    onClose()
  }}>
    <DialogTitle>Créer une nouvelle playlist</DialogTitle>
    <DialogContent>
      {/* <DialogContentText>
      </DialogContentText> */}
      <TextField
        autoFocus
        margin='dense'
        label='Nom de la playlist'
        fullWidth
        value={name}
        onChange={(evt) => setName(evt.target.value)}
      />
    </DialogContent>
    <DialogActions>
      <Button color='primary' onClick={onClose}>Annuler</Button>
      <Button color='primary' onClick={() => {
        onValid(name)
        setName('')
        onClose()
      }}>Creer</Button>
    </DialogActions>
  </Dialog>
}